import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  BankingValueObjectsTransferStatusEnum,
  PaymentRequestItemTransferFieldsFragment,
  PaymentRequestItemTransfersDocument,
} from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import CustomTwoColumnsList from 'modules/attendance/components/CustomTwoColumnsList'
import { VerticalTimeline } from 'modules/banking/components/VerticalTimeline'
import { statusLabelType, statusText } from 'modules/banking/lib/mappers'
import AmountWithCurrency from 'modules/expenses/lib/transactions/AmountWithCurrency'
import PageLoading from 'modules/layout/components/Page/Loading'

type IconColor = 'grey600' | 'viridian800' | 'malibu1000' | 'radicalRed800'

const getRejectionReasonLabel = (reason: string | null | undefined): string => {
  if (!reason) return '-'

  const reasonMap: Record<string, string> = {
    debtor_error: i18n.t('banking.transfers.rejectionReasons.debtorError'),
    creditor_error: i18n.t('banking.transfers.rejectionReasons.creditorError'),
    card_error: i18n.t('banking.transfers.rejectionReasons.cardError'),
    factorial_error: i18n.t('banking.transfers.rejectionReasons.factorialError'),
    transfer_error: i18n.t('banking.transfers.rejectionReasons.transferError'),
    provider_error: i18n.t('banking.transfers.rejectionReasons.providerError'),
    unknown_error: i18n.t('banking.transfers.rejectionReasons.unknownError'),
    technical_error: i18n.t('banking.transfers.rejectionReasons.technicalError'),
    rejected_consent: i18n.t('banking.transfers.rejectionReasons.rejectedConsent'),
  }
  return reasonMap[reason] ?? reason
}

const getStatusIconColor = (status: BankingValueObjectsTransferStatusEnum): IconColor => {
  switch (status) {
    case 'paid':
      return 'viridian800'
    case 'rejected':
      return 'radicalRed800'
    case 'in_progress':
      return 'malibu1000'
    case 'marking_as_paid':
      return 'viridian800'
    case 'init':
    default:
      return 'grey600'
  }
}

const TransferContent = ({ transfer }: { transfer: PaymentRequestItemTransferFieldsFragment }) => {
  const isRejected = transfer.status === 'rejected'

  const listItems = [
    {
      title: i18n.t('banking.attributes.amount'),
      info: <AmountWithCurrency amount={transfer.amountCents} currency={transfer.currency} />,
    },
    {
      title: i18n.t('banking.attributes.created_at'),
      info: dateBackendDateTimeToUI(transfer.createdAt),
    },
    ...(isRejected
      ? [
          {
            title: i18n.t('banking.transfers.rejectionReason'),
            info: getRejectionReasonLabel(transfer.rejectionReason),
          },
        ]
      : []),
  ]

  return <CustomTwoColumnsList list={listItems} />
}

export interface PaymentRequestItemTransfersSidePanelProps {
  paymentRequestItemId: string
  beneficiaryName: string
  onClose: () => void
}

export const PaymentRequestItemTransfersSidePanel = ({
  paymentRequestItemId,
  beneficiaryName,
  onClose,
}: PaymentRequestItemTransfersSidePanelProps) => {
  const { company } = useCurrentSession()

  const { data, loading, error } = useQuery(PaymentRequestItemTransfersDocument, {
    variables: {
      paymentRequestItemIds: [paymentRequestItemId],
    },
    skip: !paymentRequestItemId || !company.id,
  })

  const transfers =
    data?.banking?.transfersConnection?.edges?.map(
      (edge: { node: PaymentRequestItemTransferFieldsFragment }) => edge.node
    ) ?? []

  const content = () => {
    if (loading) {
      return <PageLoading />
    }

    if (error || transfers.length === 0) {
      return <NotFound />
    }

    return (
      <Box width="full" gap="s24">
        <Box width="full" gap="s8">
          <Text.Normal weight="medium" size="200" color="grey700">
            {i18n.t('banking.paymentRequests.itemTransfersSidePanel.beneficiary')}
          </Text.Normal>
          <Text.Header2 color="grey1000">{beneficiaryName}</Text.Header2>
        </Box>

        <Box width="full" gap="s8">
          <Text.Normal weight="semibold" color="grey1000">
            {i18n.t('banking.paymentRequests.itemTransfersSidePanel.transfersTimeline')}
          </Text.Normal>
          <Box width="full" paddingTop="s8">
            <VerticalTimeline
              items={transfers}
              getKey={(t) => t.id}
              getTitle={(t) => t.beneficiaryName ?? '-'}
              getStatusLabel={(t) => statusText(t.status)}
              getStatusType={(t) => statusLabelType(t.status)}
              getIconColor={(t) => getStatusIconColor(t.status)}
              renderContent={(t) => <TransferContent transfer={t} />}
            />
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Sidepanel
      overlay
      boxPadding
      width="wide"
      title={i18n.t('banking.paymentRequests.itemTransfersSidePanel.title')}
      open
      onClose={onClose}
      headerBorder
      closeLabel={i18n.t('common.close')}
    >
      {content()}
    </Sidepanel>
  )
}
